import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, fmtDate, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, Plus, Edit } from "lucide-react";

const CLAIM_STATUS = ["registered", "documents_pending", "under_review", "survey", "additional_info", "approved", "rejected", "settled", "closed"];

export default function Claims() {
  const { hasPerm } = useAuth();
  const [status, setStatus] = useState("all");
  const [policies, setPolicies] = useState([]);
  const params = status !== "all" ? { status } : {};
  const { items, total, loading, page, setPage, refetch } = useList("/claims", params);
  useEffect(() => { api.get("/policies", { params: { limit: 200 } }).then(({ data }) => setPolicies(data.items || [])).catch(() => {}); }, []);

  const create = async (v) => {
    const pol = policies.find((p) => p.id === v.policy_id);
    await api.post("/claims", { ...v, customer_id: pol?.customer_id, policy_number: pol?.policy_number, customer_name: pol?.customer_name, insurer: pol?.insurer });
    refetch();
  };
  const edit = async (c, v) => { await api.put(`/claims/${c.id}`, v); refetch(); };

  const createFields = [
    { name: "policy_id", label: "Policy", type: "select", required: true, options: policies.map((p) => ({ value: p.id, label: `${p.policy_number} — ${p.customer_name || ""} (${p.insurer})` })) },
    { name: "claim_type", label: "Claim Type", type: "select", options: ["Accident", "Theft", "Third Party", "Own Damage", "Fire", "Natural Calamity", "Health", "Other"] },
    { name: "incident_date", label: "Incident Date", type: "date" },
    { name: "claim_amount", label: "Claim Amount (₹)", type: "number" },
    { name: "surveyor", label: "Surveyor" },
    { name: "claim_status", label: "Status", type: "select", options: CLAIM_STATUS.map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "notes", label: "Notes", type: "textarea", full: true },
  ];
  const editFields = [
    { name: "claim_status", label: "Claim Status", type: "select", required: true, options: CLAIM_STATUS.map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "approved_amount", label: "Approved Amount (₹)", type: "number" },
    { name: "rejected_amount", label: "Rejected Amount (₹)", type: "number" },
    { name: "rejection_reason", label: "Rejection Reason" },
    { name: "settlement_date", label: "Settlement Date", type: "date" },
    { name: "notes", label: "Notes", type: "textarea", full: true },
  ];
  const columns = [
    { key: "claim_number", header: "Claim No", render: (r) => <span className="font-mono text-xs">{r.claim_number}</span> },
    { key: "customer_name", header: "Customer", render: (r) => <span className="font-semibold">{r.customer_name || "—"}</span> },
    { key: "policy_number", header: "Policy", render: (r) => <span className="font-mono text-xs">{r.policy_number || "—"}</span> },
    { key: "claim_type", header: "Type", render: (r) => <span className="text-sm">{r.claim_type || "—"}</span> },
    { key: "claim_amount", header: "Amount", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular">{INR(r.claim_amount)}</span> },
    { key: "claim_status", header: "Status", render: (r) => <StatusBadge value={r.claim_status} /> },
    { key: "claim_date", header: "Filed", render: (r) => <span className="text-sm">{fmtDate(r.claim_date)}</span> },
    ...(hasPerm("claims:edit") ? [{ key: "actions", header: "", render: (r) => (
      <div onClick={(e) => e.stopPropagation()}>
        <FormDialog title={`Update Claim — ${r.claim_number}`} fields={editFields} initial={{ claim_status: r.claim_status }} onSubmit={(v) => edit(r, v)} submitLabel="Update"
          trigger={<Button size="icon" variant="ghost" className="h-8 w-8" data-testid="claim-edit-btn"><Edit className="h-4 w-4" /></Button>} />
      </div>
    ) }] : []),
  ];
  return (
    <div>
      <PageHeader title="Claims" subtitle={`${total} claims tracked`} icon={LifeBuoy}
        actions={hasPerm("claims:add") && (
          <FormDialog title="Register Claim" fields={createFields} onSubmit={create} submitLabel="Register Claim" testId="create-claim-dialog"
            trigger={<Button data-testid="create-claim-btn"><Plus className="h-4 w-4 mr-1.5" /> Register Claim</Button>} />
        )} />
      <div className="flex gap-3 mb-4">
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-52" data-testid="claim-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{CLAIM_STATUS.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent></Select>
      </div>
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="claim-table-row" emptyText="No claims registered" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
